<template>
  <div class="page-container">
    <el-breadcrumb separator="/">
      <el-breadcrumb-item>
        <span class="page-title">公共账号管理</span>
      </el-breadcrumb-item>
      <el-breadcrumb-item>流水统计</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card class="page-content" shadow="never">
      <div class="page-actions">
        <el-date-picker
          v-model="dateRange"
          size="mini"
          type="daterange"
          align="right"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          format="yyyy-MM-dd"
          value-format="yyyy-MM-dd"
          clearable
          @change="handleSearch"
        />
        <el-select
          v-model="coin"
          size="mini"
          placeholder="币种"
          filterable
          style="width: 120px"
          @change="handleSearch"
        >
          <el-option v-for="item in coinOptions" :key="item" :label="item" :value="item" />
        </el-select>
        <el-button size="mini" type="primary" @click="handleSearch">搜索</el-button>
        <div class="page-actions-right">
          <el-popover placement="bottom-end" width="320" trigger="click" popper-class="bill-type-filter-popover">
            <div class="type-filter-panel">
              <div class="type-filter-toolbar">
                <el-checkbox
                  :indeterminate="typeCheckIndeterminate"
                  v-model="typeCheckAll"
                  @change="handleTypeCheckAll"
                >
                  全选
                </el-checkbox>
                <el-button type="text" size="mini" @click.native.stop="resetVisibleTypes">恢复默认</el-button>
              </div>
              <el-checkbox-group v-model="selectedBillTypes" class="type-filter-group" @change="onVisibleTypesChange">
                <el-checkbox
                  v-for="item in allTypeColumns"
                  :key="item.billType"
                  :label="item.billType"
                  class="type-filter-item"
                >
                  {{ item.billTypeName }}
                </el-checkbox>
              </el-checkbox-group>
            </div>
            <el-button slot="reference" size="mini">
              筛选类型（{{ selectedBillTypes.length }}/{{ allTypeColumns.length }}）
            </el-button>
          </el-popover>
        </div>
      </div>

      <div class="total-box" v-if="displayTotalItems.length">
        <div
          class="total-item"
          v-for="item in displayTotalItems"
          :key="item.billType"
          :class="{ 'total-item-net-profit': item.billType === netProfitBillType }"
        >
          <span>{{ item.billTypeName }}</span>
          <span
            :class="[
              amountClass(item.netAmount),
              item.billType !== netProfitBillType ? 'clickable-amount' : '',
            ]"
            @click="onTotalItemClick(item)"
          >
            {{ item.billType === netProfitBillType ? '' : '净额 ' }}{{ formatSignedAmount(item.netAmount) }}
          </span>
        </div>
      </div>

      <el-table :data="tableRows" style="width: 100%" min-height="500">
        <el-table-column prop="statDate" label="日期" width="120" fixed header-align="left" align="left" />
        <el-table-column prop="coin" label="币种" width="100" fixed header-align="left" align="left" />
        <el-table-column
          v-for="col in visibleTypeColumns"
          :key="col.billType"
          :label="col.billTypeName"
          min-width="140"
          header-align="left"
          align="left"
        >
          <template slot-scope="scope">
            <span
              :class="['clickable-amount', amountClass(cellNetAmount(scope.row, col.billType))]"
              @click="openDetailFromCell(scope.row, col.billType)"
            >
              {{ formatAmount(cellNetAmount(scope.row, col.billType)) }}
            </span>
          </template>
        </el-table-column>
      </el-table>

      <pagination
        :size="pagination.size"
        :page="pagination.page"
        :total="pagination.total"
        @pageChanged="handlePageChanged"
      />
    </el-card>

    <detail-dialog ref="detailDialog" />
  </div>
</template>

<script>
import Pagination from 'components/pagination';
import api from './api';
import DetailDialog from './components/DetailDialog';
import BigNumber from 'bignumber.js';
import {
  formatPlainAmount,
  formatSignedAmount as formatSignedAmountText,
  sumPlainAmounts,
} from '../common/amount';
import { DEFAULT_COIN, loadCoinOptions } from '../common/coins';
import {
  DEFAULT_VISIBLE_BILL_TYPES,
  NET_PROFIT_BILL_TYPE,
  NET_PROFIT_EXCLUDED_BILL_TYPES,
  defaultVisibleBillTypes,
  loadVisibleBillTypes,
  saveVisibleBillTypes,
  VISIBLE_TYPES_STORAGE_KEY,
} from './billTypeConfig';

const emptyAmount = () => ({
  addAmount: 0,
  subAmount: 0,
  netAmount: 0,
});

export default {
  name: 'PublicAccountBillStatistic',
  components: {
    DetailDialog,
    Pagination,
  },
  data() {
    return {
      dateRange: [],
      coin: DEFAULT_COIN,
      coinOptions: [DEFAULT_COIN],
      allTypeColumns: [],
      selectedBillTypes: [...DEFAULT_VISIBLE_BILL_TYPES],
      typeCheckAll: false,
      typeCheckIndeterminate: true,
      totalItems: [],
      netProfitBillType: NET_PROFIT_BILL_TYPE,
      /** 防止切换币种后旧汇总请求覆盖新数据 */
      searchToken: 0,
      tableRows: [],
      pagination: {
        page: 1,
        size: 20,
        total: 0,
      },
    };
  },
  computed: {
    visibleTypeColumns() {
      const selected = new Set(this.selectedBillTypes);
      return this.allTypeColumns.filter((col) => selected.has(col.billType));
    },
    visibleTotalItems() {
      const selected = new Set(this.selectedBillTypes);
      return this.totalItems.filter((item) => selected.has(item.billType));
    },
    netProfitAmount() {
      try {
        const excluded = new Set(NET_PROFIT_EXCLUDED_BILL_TYPES);
        const picked = this.visibleTotalItems.filter((item) => !excluded.has(item.billType));
        return sumPlainAmounts(picked, (item) => item.netAmount);
      } catch (e) {
        return '0';
      }
    },
    displayTotalItems() {
      const items = this.visibleTotalItems;
      if (!items.length && !this.totalItems.length) {
        return [];
      }
      const netProfit = {
        billType: NET_PROFIT_BILL_TYPE,
        billTypeName: '净利润',
        netAmount: this.netProfitAmount,
      };
      if (!items.length) {
        return [netProfit];
      }
      return [netProfit, ...items];
    },
  },
  mounted() {
    Promise.all([this.loadTypes(), loadCoinOptions()]).then(([, coins]) => {
      this.coinOptions = coins;
      if (!this.coinOptions.includes(this.coin)) {
        this.coin = DEFAULT_COIN;
      }
      this.handleSearch();
    });
  },
  methods: {
    formatAmount(value) {
      return formatPlainAmount(value);
    },
    formatSignedAmount(value) {
      return formatSignedAmountText(value);
    },
    amountClass(value) {
      try {
        return new BigNumber(value || 0).gte(0) ? 'amount-positive' : 'amount-negative';
      } catch (e) {
        return 'amount-positive';
      }
    },
    cellNetAmount(row, billType) {
      const cell = row.amountMap && row.amountMap[billType];
      return cell ? cell.netAmount : 0;
    },
    queryParams() {
      return {
        startTime: this.dateRange && this.dateRange[0] ? this.dateRange[0] : '',
        endTime: this.dateRange && this.dateRange[1] ? this.dateRange[1] : '',
        coin: this.coin,
      };
    },
    loadTypes() {
      return api.types().then((res) => {
        if (res.code === 0) {
          this.allTypeColumns = res.data || [];
          this.selectedBillTypes = loadVisibleBillTypes(this.allTypeColumns);
          this.syncTypeCheckState();
        }
      });
    },
    syncTypeCheckState() {
      const total = this.allTypeColumns.length;
      const selected = this.selectedBillTypes.length;
      this.typeCheckAll = total > 0 && selected === total;
      this.typeCheckIndeterminate = selected > 0 && selected < total;
    },
    handleTypeCheckAll(checked) {
      const next = checked
        ? this.allTypeColumns.map((col) => col.billType)
        : [];
      this.selectedBillTypes = next.slice();
      this.onVisibleTypesChange();
    },
    resetVisibleTypes() {
      try {
        localStorage.removeItem(VISIBLE_TYPES_STORAGE_KEY);
      } catch (e) {
        // ignore
      }
      this.selectedBillTypes = defaultVisibleBillTypes(this.allTypeColumns).slice();
      this.onVisibleTypesChange();
    },
    onVisibleTypesChange() {
      this.syncTypeCheckState();
      saveVisibleBillTypes(this.selectedBillTypes);
    },
    buildRow(row) {
      const amountMap = {};
      this.allTypeColumns.forEach((col) => {
        amountMap[col.billType] = emptyAmount();
      });
      (row.items || []).forEach((item) => {
        amountMap[item.billType] = {
          addAmount: item.addAmount,
          subAmount: item.subAmount,
          netAmount: item.netAmount,
        };
      });
      return {
        statDate: row.statDate,
        coin: row.coin,
        amountMap,
      };
    },
    openDetailFromCell(row, billType) {
      this.$refs.detailDialog.open({
        statDate: row.statDate,
        billType,
        coin: row.coin,
      });
    },
    onTotalItemClick(item) {
      if (item.billType === NET_PROFIT_BILL_TYPE) {
        return;
      }
      this.openDetailFromTotal(item);
    },
    openDetailFromTotal(item) {
      const { startTime, endTime } = this.queryParams();
      if (!startTime || !endTime) {
        this.$message.warning('请先选择日期范围后再查看汇总明细');
        return;
      }
      this.$refs.detailDialog.open({
        startTime,
        endTime,
        billType: item.billType,
        coin: this.coin,
      });
    },
    fetchTotal(token) {
      const params = this.queryParams();
      return api.total(params).then((res) => {
        if (token !== this.searchToken) {
          return;
        }
        if (res.code === 0) {
          this.totalItems = (res.data && res.data.items) || [];
        }
      });
    },
    fetchPage(page, size, token) {
      const params = { ...this.queryParams(), page, size };
      return api.page(params).then((res) => {
        if (token !== this.searchToken) {
          return;
        }
        if (res.code !== 0) return;
        const records = (res.data && res.data.list) || [];
        this.tableRows = records.map((row) => this.buildRow(row));
        this.pagination.total = (res.data && res.data.totalCount) || 0;
        this.pagination.page = page;
        this.pagination.size = size;
      });
    },
    handleSearch() {
      this.searchToken += 1;
      const token = this.searchToken;
      this.pagination.page = 1;
      this.totalItems = [];
      this.tableRows = [];
      Promise.all([
        this.fetchTotal(token),
        this.fetchPage(1, this.pagination.size, token),
      ]);
    },
    handlePageChanged({ page, size }) {
      const token = this.searchToken;
      this.fetchPage(page, size, token);
    },
  },
};
</script>

<style scoped>
.page-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: nowrap;
}

.page-actions-right {
  margin-left: auto;
  flex-shrink: 0;
}

.type-filter-panel {
  max-height: 360px;
  overflow-y: auto;
}

.type-filter-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #ebeef5;
}

.type-filter-group {
  display: flex;
  flex-direction: column;
}

.type-filter-item {
  margin: 4px 0;
  line-height: 1.4;
}

.total-box {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.total-item {
  display: flex;
  flex-direction: column;
  min-width: 140px;
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 12px;
}

.total-item-net-profit {
  background: #ecf5ff;
  border: 1px solid #d9ecff;
}

.clickable-amount {
  cursor: pointer;
}

.clickable-amount:hover {
  text-decoration: underline;
}

.amount-positive {
  color: #67c23a;
}

.amount-negative {
  color: #f56c6c;
}
</style>
